/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Footer from '../components/Footer';

const BlogsPage = () => {
  const [expandedBlog, setExpandedBlog] = useState(null);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BLOGS = process.env.REACT_APP_API_BASE_URL + "/UIblogs";
  const API_MEDIA = process.env.REACT_APP_API_BASE_URL + "/UImedia";

  // Fetch blogs from API
  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_BLOGS);
      let blogsData = res.data.results || res.data.data || res.data || [];

      // If blogsData is not an array, convert it to array
      if (!Array.isArray(blogsData)) {
        blogsData = [blogsData];
      }

      // Load images for each blog
      const blogsWithImages = await Promise.all(
        blogsData.map(async (blog) => {
          if (blog.img?.id) {
            try {
              const imgRes = await axios.get(`${API_MEDIA}/${blog.img.id}/download`, {
                responseType: "blob",
              });
              blog.imageUrl = URL.createObjectURL(imgRes.data);
            } catch (err) {
              console.error("Error loading image for", blog.title, err);
              blog.imageUrl = "https://via.placeholder.com/400x200.png?text=No+Image";
            }
          } else {
            blog.imageUrl = "https://via.placeholder.com/400x200.png?text=No+Image";
          }
          return blog;
        })
      );

      // Filter only active blogs
      const activeBlogs = blogsWithImages.filter(blog => blog.isActive !== false);
      setBlogPosts(activeBlogs);
    } catch (err) {
      console.error("Error fetching blogs:", err);
      setError("Failed to load blogs. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  // Toggle blog expansion
  const toggleBlogExpansion = (blogId) => {
    setExpandedBlog(expandedBlog === blogId ? null : blogId);
  };

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown Date';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        // If it's already a formatted string, return as is
        return dateString;
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-teal-400 mx-auto mb-4"></div>
          <p className="text-lg text-gray-300">Loading blogs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 text-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-white mb-2">Oops! Something went wrong</h2>
          <p className="text-gray-300 mb-4">{error}</p>
          <button
            onClick={fetchBlogs}
            className="bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      {/* Hero Section */}
      <section className="relative h-72 bg-gray-800">
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="relative z-10 flex items-center justify-center h-full">
          <div className="text-center">
            <h1 className="text-5xl font-extrabold text-white mb-2">
              MASA <span className="text-teal-400">Blog</span>
            </h1>
            <p className="text-lg text-gray-300 max-w-lg mx-auto">
              Dive into insights, tips, and stories from our community.
            </p>
            <div className="mt-4 text-amber-400">
              {blogPosts.length} {blogPosts.length === 1 ? 'article' : 'articles'} published
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Section */}
      <section className="container mx-auto px-6 py-16">
        {blogPosts.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">📝</div>
            <h2 className="text-2xl font-bold text-white mb-2">No Blogs Available</h2>
            <p className="text-gray-300">Check back later for new articles and insights.</p>
          </div>
        ) : (
          <div className="space-y-10">
            {blogPosts.map((post) => (
              <div
                key={post.id || post._id}
                className="bg-gray-800 rounded-lg overflow-hidden shadow-lg transition-all duration-300 hover:shadow-xl hover:transform hover:scale-[1.02]"
              >
                <div className="flex flex-col md:flex-row">
                  <div className="md:w-1/3 h-64">
                    <img
                      src={post.imageUrl}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                  </div>
                  <div className="md:w-2/3 p-6">
                    <div className="flex items-center text-sm text-gray-400 mb-3">
                      <span>{formatDate(post.date)}</span>
                      <span className="mx-2">•</span>
                      <span className="text-white capitalize">{post.author || 'MASA Forum'}</span>
                      {post.userId?.name && (
                        <>
                          <span className="mx-2">•</span>
                          <span className="text-teal-400">By {post.userId.name}</span>
                        </>
                      )}
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-3">{post.title}</h2>
                    <p className="text-gray-300 mb-4">
                      {expandedBlog === (post.id || post._id) ? '' : (post.excerpt || 'Click "Read More" to view the full article.')}
                    </p>

                    {expandedBlog === (post.id || post._id) && (
                      <div className="mt-4 text-gray-300 leading-relaxed animate-fade-in space-y-4">
                        <div className="prose prose-invert max-w-none">
                          {post.fullContent?.split('\n').map((paragraph, index) => (
                            <p key={index} className="mb-4">
                              {paragraph}
                            </p>
                          )) || post.fullContent}
                        </div>
                      </div>
                    )}

                    <button
                      className={`text-teal-400 hover:text-teal-300 font-semibold transition-colors duration-300 flex items-center gap-2 ${
                        expandedBlog === (post.id || post._id) ? 'mt-4' : ''
                      }`}
                      onClick={() => toggleBlogExpansion(post.id || post._id)}
                    >
                      {expandedBlog === (post.id || post._id) ? (
                        <>
                          <span>Show Less</span>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                          </svg>
                        </>
                      ) : (
                        <>
                          <span>Read More</span>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default BlogsPage;


// old code static version below
// import React, { useState } from 'react';
// import Footer from '../components/Footer';


// const BlogsPage = () => {
//   const [expandedBlog, setExpandedBlog] = useState(null); // Track expanded blog by ID

//   // Sample blog data with full content
//   const blogPosts = [
//      {
//       id: 1,
//       title: "Unlocking MSME Growth Through TReDS: A Call for Inclusive Credit Access",
//       excerpt: "The Trade Receivables Discounting System (TReDS) has emerged as a game-changer in facilitating timely and collateral-free credit for MSMEs",
//       fullContent: " The platform’s consistent growth, especially with operators like M1xchange, highlights its increasing relevance in bridging the working capital gap.Yet, it is concerning that TReDS still addresses less than 5% of the sector’s massive credit demand. For a country with over 64 million MSMEs and a credit gap of Rs 20–25 lakh crore, this signals the need for deeper reforms and expanded outreach. MASA Forum strongly supports further innovations—such as secondary market trading of invoices, integration with GSTN, and alternative credit assessment models—to strengthen this ecosystem. We urge all stakeholders to work in unison to make TReDS a mainstream financing channel that empowers even the smallest MSME in the remotest corner of India.",
//       date: "May 19, 2025",
//       author: "sushil poddar",
//       image: "https://res.cloudinary.com/dnmlijyox/image/upload/blog-imgs/president.webp",
//     },
//      {
//       id: 6,
//       title: "Empowering India's MSMEs Through Digital Transformation",
//       excerpt: "Introduction Micro, Small, and Medium Enterprises (MSMEs) are the heart of India's economic engine. Yet, in today’s digital-first world, many MSMEs still operate with traditional methods. The MASA Forum is on a mission to change that — one business at a time.",
//       fullContent: "The Digital Imperative With tools like Udyam Registration, GeM (Government eMarketplace), and digital accounting platforms such as Zoho and TallyPrime, MSMEs can reduce paperwork, cut costs, and improve operational efficiency. How MASA Forum Helps MASA offers tech mentoring sessions, access to software tools at discounted rates, and workshops on eCommerce onboarding. We also help with integration into India Stack APIs like Aadhaar, UPI, and Digilocker for smoother compliance and financial inclusion.",
//       date: "May 19, 2025",
//       author: "masa forum",
//       image: "https://res.cloudinary.com/dnmlijyox/image/upload/blog-imgs/empower.webp",
//     },
//      {
//       id: 7,
//       title: "Green Growth for MSMEs: Sustainable Business is Profitable Business",
//       excerpt: "Sustainability is no longer just a buzzword — it's a necessity. Indian MSMEs must now align with ESG (Environmental, Social, Governance) goals to remain compliant and globally competitive.",
//       fullContent: "The Green Advantage Eco-conscious businesses get easier credit access, carbon-offset tax benefits, and eligibility for government schemes like the Zero Effect, Zero Defect (ZED) program.MASA Forum’s Green Drive Through our Green MSME Bootcamps, MASA trains small manufacturers and service providers in energy efficiency, waste management, and green certification (such as ISO 14001 and ZED Bronze/Silver)..",
//       date: "May 19, 2025",
//       author: "masa forum",
//       image: "https://res.cloudinary.com/dnmlijyox/image/upload/blog-imgs/greengrowth.webp",
//     },

//     {
//       id: 8,
//       title: "From Idea to Impact: How MASA Forum is Accelerating MSME & Startup Success in Tier 2 and 3 Cities",
//       excerpt: "Innovation isn't limited to metro cities. Today, India’s Tier 2 and Tier 3 cities are bustling with entrepreneurial talent. MASA Forum is actively building startup ecosystems in cities like Kolkata, Raipur and Bhopal.",
//       fullContent: "Startups in smaller cities face barriers like limited access to mentorship, investors, and incubation spaces. MASA’s hybrid model of virtual incubation, local networking events, and remote pitch days is solving this. Real Stories, Real Impact Startups from small towns we’ve supported include a healthtech firm from Bareilly now exporting diagnostic kits, and a smart irrigation company from Bhubaneswar that secured a ₹25 lakh angel round.",
//       date: "May 19, 2025",
//       author: "masa forum",
//       image: "https://res.cloudinary.com/dnmlijyox/image/upload/blog-imgs/impact.webp",
//     },
//     {
//       id: 5,
//       title: "The Future of Startups in 2025",
//       excerpt: "Exploring trends and predictions for the startup ecosystem in the coming year.",
//       fullContent: "The startup landscape in 2025 is poised for significant evolution. With advancements in AI, blockchain, and sustainable tech, entrepreneurs are finding new opportunities to innovate. This article explores key trends, including the rise of remote-first companies, increased investor focus on ESG (Environmental, Social, Governance) factors, and the growing importance of community-driven business models. We’ll also discuss predictions from industry experts and how startups can position themselves for success in this dynamic environment.",
//       date: "March 25, 2025",
//       author: "Ira Saha",
//       image: "https://res.cloudinary.com/dnmlijyox/image/upload/blog-imgs/ira.JPG.webp",
//     },
//     {
//       id: 3,
//       title: "AI Revolution: What It Means for Entrepreneurs",
//       excerpt: "A deep dive into how AI is reshaping business opportunities.",
//       fullContent: "Artificial Intelligence is no longer a futuristic concept—it’s here, and it’s transforming entrepreneurship. From automating mundane tasks to providing deep customer insights, AI offers tools that level the playing field for small businesses. This article dives into practical applications of AI for entrepreneurs, such as predictive analytics, personalized marketing, and operational efficiency. We’ll also explore the ethical considerations and future implications of this tech revolution.",
//       date: "March 15, 2025",
//       author: "Biswarup Chatterjee",
//       image: "https://res.cloudinary.com/dnmlijyox/image/upload/blog-imgs/biswarup.JPG.webp",
//     },
//     {
//       id: 4,
//       title: "Scaling Your Business: Lessons Learned",
//       excerpt: "Key takeaways from successful founders on growing sustainably.",
//       fullContent: "Scaling a business is a delicate balance of growth and stability. In this post, we share lessons from founders who’ve successfully scaled their ventures. Topics include hiring the right team, maintaining company culture, leveraging data-driven decisions, and securing funding without losing control. These insights are backed by real-world case studies, offering actionable advice for entrepreneurs at any stage of their journey.",
//       date: "March 10, 2025",
//       author: "Pragya Jhunjhunwala",
//       image: "https://res.cloudinary.com/dnmlijyox/image/upload/blog-imgs/pragya.JPG.webp",
//     },
//     {
//       id: 2,
//       title: "How to Pitch to Investors Like a Pro",
//       excerpt: "Tips and strategies to nail your next investor pitch.",
//       fullContent: "Pitching to investors can be daunting, but with the right approach, you can turn it into a winning opportunity. This post covers essential tips: crafting a compelling story, focusing on your unique value proposition, preparing for tough questions, and mastering your delivery. Learn from real-world examples of successful pitches and avoid common pitfalls that turn investors away. Whether you’re a first-time founder or a seasoned entrepreneur, these strategies will help you shine.",
//       date: "March 20, 2025",
//       author: "Vibhor Tandon",
//       image: "https://res.cloudinary.com/dnmlijyox/image/upload/blog-imgs/tandon.webp",
//     },
     
    
//   ];

//   // Toggle blog expansion
//   const toggleBlogExpansion = (blogId) => {
//     setExpandedBlog(expandedBlog === blogId ? null : blogId);
//   };

//   return (
//     <div className="min-h-screen bg-gray-900 text-gray-100">
//       {/* Hero Section */}
//       <section className="relative h-72 bg-gray-800 ">
//         <div className="absolute inset-0 bg-black opacity-50"></div>
//         <div className="relative z-10 flex items-center justify-center h-full">
//           <div className="text-center">
//             <h1 className="text-5xl font-extrabold text-white mb-2">
//               MASA <span className="text-teal-400">Blog</span>
//             </h1>
//             <p className="text-lg text-gray-300 max-w-lg mx-auto">
//               Dive into insights, tips, and stories from our community.
//             </p>
//           </div>
//         </div>
//       </section>

//       {/* Blog Posts Section */}
//       <section className="container mx-auto px-6 py-16">
//         <div className="space-y-10">
//           {blogPosts.map((post) => (
//             <div
//               key={post.id}
//               className="bg-gray-800 rounded-lg overflow-hidden shadow-lg transition-all duration-300 hover:shadow-xl"
//             >
//               <div className="flex flex-col md:flex-row">
//                 <div className="md:w-1/3 h-64">
//                   <img
//                     src={post.image}
//                     alt={post.title}
//                     className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
//                   />
//                 </div>
//                 <div className="md:w-2/3 p-6">
//                   <div className="flex items-center text-sm text-gray-400 mb-3">
//                     <span>{post.date}</span>
//                     <span className="mx-2">•</span>
//                     <span className="text-white">{post.author}</span>
//                   </div>
//                   <h2 className="text-2xl font-bold text-white mb-3">{post.title}</h2>
//                   <p className="text-gray-300 mb-4">
//                     {expandedBlog === post.id ? '' : post.excerpt}
//                   </p>

//                   {expandedBlog === post.id && (
//                     <div className="mt-4 text-gray-300 leading-relaxed animate-fade-in">
//                       {post.fullContent}
//                     </div>
//                   )}

//                   <button
//                     className={`text-teal-400 hover:text-teal-300 font-semibold transition-colors duration-300 ${
//                       expandedBlog === post.id ? 'mt-4' : ''
//                     }`}
//                     onClick={() => toggleBlogExpansion(post.id)}
//                   >
//                     {expandedBlog === post.id ? 'Show Less' : 'Read More'}
//                   </button>
//                 </div>
//               </div>
//             </div>
//           ))}
//         </div>
//       </section>

//       <Footer />
//     </div>
//   );
// };

// export default BlogsPage;